// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Privacy UI — the Tools → Privacy panel.
 *
 * Small modal dialog housing:
 *   - a radio group for the telemetry tier (Essential / Research / Off)
 *   - a read-only display of the current in-memory session ID
 *   - a link to the full policy at `/privacy`
 *   - a polite live-region for status announcements
 *
 * The panel is lazy-mounted on first `openPrivacyUI()` and kept in
 * the DOM afterwards so repeat opens are cheap and focus/aria state
 * stays predictable. Escape + outside-click close it.
 *
 * Tier changes persist via `setTier()` and apply the runtime buffer
 * consequences via `applyTierChange()` (drop all on Off, strip
 * Tier B on Research → Essential). The emitted `settings_changed`
 * event carries the key `telemetry_tier` and a `value_class` of
 * the new tier; per the sanitizer rules the tier itself is safe
 * to emit verbatim as it is a fixed enum.
 */

import {
  applyTierChange,
  emit,
  getSessionId,
  loadConfig,
  setTier,
} from '../analytics'
import type { TelemetryTier } from '../types'
import { t, tAttr, tHtml, type MessageKey } from '../i18n'
import { escapeHtml } from './domUtils'
import { clearVisits, isVisitMemoryOn, setVisitMemoryOn, visitCount } from '../services/visitMemory'

let mounted = false
let isOpen = false
let lastTrigger: HTMLElement | null = null
let listenerController: AbortController | null = null

/** Tier → MessageKey lookup. Lives at module scope so the type-
 *  check verifies all three tiers map to valid keys, with no
 *  template-string casting. Compile-time enforcement that we
 *  haven't dropped a tier on either axis. */
const TIER_LABEL_KEY: Record<TelemetryTier, MessageKey> = {
  essential: 'privacy.tier.essential.label',
  research: 'privacy.tier.research.label',
  off: 'privacy.tier.off.label',
}
const TIER_DESC_KEY: Record<TelemetryTier, MessageKey> = {
  essential: 'privacy.tier.essential.desc',
  research: 'privacy.tier.research.desc',
  off: 'privacy.tier.off.desc',
}

/** Resolve tier label (`Essential` / `Research` / `Off`) at call time
 * so a locale switch + reload picks up new copy without a rebuild. */
function tierLabel(tier: TelemetryTier): string {
  return t(TIER_LABEL_KEY[tier])
}
function tierDesc(tier: TelemetryTier): string {
  return t(TIER_DESC_KEY[tier])
}

/** Build the panel HTML. Called once on first open. */
function buildPanel(): HTMLElement {
  const host = document.createElement('div')
  host.id = 'privacy-ui-host'
  host.innerHTML = `
    <div id="privacy-ui-backdrop" class="privacy-ui-backdrop hidden" aria-hidden="true"></div>
    <section
      id="privacy-ui-panel"
      class="privacy-ui-panel hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-ui-title"
      aria-describedby="privacy-ui-desc"
    >
      <header class="privacy-ui-header">
        <h2 id="privacy-ui-title">${tHtml('privacy.title')}</h2>
        <button
          type="button"
          id="privacy-ui-close"
          class="privacy-ui-close"
          aria-label="${tAttr('privacy.close.aria')}"
        >&#x2715;</button>
      </header>
      <p id="privacy-ui-desc" class="privacy-ui-desc">${tHtml('privacy.desc')}</p>
      <fieldset class="privacy-ui-tiers" role="radiogroup" aria-label="${tAttr('privacy.radiogroup.aria')}">
        <legend class="sr-only">${tHtml('privacy.radiogroup.aria')}</legend>
        ${renderTierOption('essential')}
        ${renderTierOption('research')}
        ${renderTierOption('off')}
      </fieldset>
      <div class="privacy-ui-meta">
        <div class="privacy-ui-session">
          <span class="privacy-ui-session-label">${tHtml('privacy.session.label')}</span>
          <code id="privacy-ui-session-id" class="privacy-ui-session-id"></code>
          <span class="privacy-ui-session-hint">${tHtml('privacy.session.hint')}</span>
        </div>
        <a class="privacy-ui-policy-link" href="/privacy" target="_blank" rel="noopener">${tHtml('privacy.policyLink')}</a>
      </div>
      <p class="privacy-ui-localnote">${tHtml('privacy.localNote.visitMemory')}</p>
      <div class="privacy-ui-actions">
        <label class="privacy-ui-visits-switch">
          <input type="checkbox" id="privacy-ui-visits-on" />
          <span>${tHtml('privacy.visits.switch')}</span>
        </label>
        <button
          type="button"
          id="privacy-ui-clear-visits"
          class="privacy-ui-clear-visits"
        >${tHtml('privacy.clearVisits.label')}</button>
      </div>
      <div id="privacy-ui-status" class="privacy-ui-status" role="status" aria-live="polite"></div>
    </section>
  `
  document.body.appendChild(host)
  return host
}

function renderTierOption(tier: TelemetryTier): string {
  return `
    <label class="privacy-ui-tier">
      <input
        type="radio"
        name="privacy-ui-tier"
        value="${tier}"
        data-tier="${tier}"
      />
      <span class="privacy-ui-tier-body">
        <span class="privacy-ui-tier-label">${escapeHtml(tierLabel(tier))}</span>
        <span class="privacy-ui-tier-desc">${escapeHtml(tierDesc(tier))}</span>
      </span>
    </label>
  `
}

/** Set the radio group to reflect the currently persisted tier. */
function syncRadios(): void {
  const current = loadConfig().tier
  const radios = document.querySelectorAll<HTMLInputElement>('input[name="privacy-ui-tier"]')
  radios.forEach((r) => {
    r.checked = r.value === current
  })
}

/** Refresh the session-ID display. Called on every open because the
 * ID rotates on app relaunch; no need to observe it continuously. */
function syncSessionId(): void {
  const el = document.getElementById('privacy-ui-session-id')
  if (el) el.textContent = getSessionId()
}

/** Refresh the "Clear visit history" button — disabled (and labelled
 * plainly) when there's nothing to clear, otherwise labelled with the
 * count so the user knows the scope of the action. Called on every
 * open and after a clear. */
function syncClearVisitsButton(): void {
  const btn = document.getElementById('privacy-ui-clear-visits') as HTMLButtonElement | null
  if (!btn) return
  const count = visitCount()
  btn.disabled = count === 0
  btn.textContent = count === 0
    ? t('privacy.clearVisits.label')
    : t('privacy.clearVisits.labelCount', { count: String(count) })
}

/** AYNI: reflect whether visit memory is recording. */
function syncVisitsSwitch(): void {
  const box = document.getElementById('privacy-ui-visits-on') as HTMLInputElement | null
  if (box) box.checked = isVisitMemoryOn()
}

/** AYNI: the visit memory switch. Off wipes what was kept, so it says so. */
function handleVisitsSwitch(on: boolean): void {
  setVisitMemoryOn(on)
  syncClearVisitsButton()
  announce(t(on ? 'privacy.visits.announce.on' : 'privacy.visits.announce.off'))
}

/** Wire up listeners on the mounted panel. Uses an AbortController
 * so re-init (tests / HMR) cleanly removes every handler. */
function wireEvents(): void {
  listenerController?.abort()
  listenerController = new AbortController()
  const { signal } = listenerController

  document
    .getElementById('privacy-ui-close')
    ?.addEventListener('click', () => closePrivacyUI(), { signal })

  document
    .getElementById('privacy-ui-backdrop')
    ?.addEventListener('click', () => closePrivacyUI(), { signal })

  document
    .getElementById('privacy-ui-clear-visits')
    ?.addEventListener('click', () => handleClearVisits(), { signal })

  const visitsSwitch = document.getElementById('privacy-ui-visits-on') as HTMLInputElement | null
  visitsSwitch?.addEventListener('change', () => handleVisitsSwitch(visitsSwitch.checked), { signal })

  const radios = document.querySelectorAll<HTMLInputElement>('input[name="privacy-ui-tier"]')
  radios.forEach((r) => {
    r.addEventListener(
      'change',
      () => {
        if (!r.checked) return
        const tier = r.value as TelemetryTier
        handleTierChange(tier)
      },
      { signal },
    )
  })

  document.addEventListener(
    'keydown',
    (ev) => {
      if (!isOpen) return
      if (ev.key === 'Escape') {
        ev.stopPropagation()
        closePrivacyUI()
      } else if (ev.key === 'Tab') {
        trapFocus(ev)
      }
    },
    { signal },
  )
}

/** Return focusable descendants of the modal panel, skipping
 * disabled controls. Doesn't bother filtering by computed style —
 * the modal is binary open/closed and contains no nested hidden
 * subtrees, so every matching element in the panel is visible
 * whenever the panel is. */
function getFocusableInPanel(): HTMLElement[] {
  const panel = document.getElementById('privacy-ui-panel')
  if (!panel) return []
  return Array.from(
    panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  )
}

/** Cycle Tab / Shift-Tab between the first and last focusable
 * elements inside the modal so keyboard focus can't escape to the
 * underlying page while the dialog is open. Required by
 * `aria-modal="true"`. */
function trapFocus(ev: KeyboardEvent): void {
  const focusables = getFocusableInPanel()
  if (focusables.length === 0) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement as HTMLElement | null
  const panel = document.getElementById('privacy-ui-panel')
  const withinPanel = !!(active && panel?.contains(active))

  if (ev.shiftKey) {
    if (!withinPanel || active === first) {
      ev.preventDefault()
      last.focus()
    }
  } else {
    if (!withinPanel || active === last) {
      ev.preventDefault()
      first.focus()
    }
  }
}

/** Persist the new tier, apply buffer consequences, announce. */
function handleTierChange(tier: TelemetryTier): void {
  setTier(tier)
  applyTierChange(tier)
  emit({
    event_type: 'settings_changed',
    key: 'telemetry_tier',
    value_class: tier,
  })
  announce(
    tier === 'off'
      ? t('privacy.announce.off')
      : t('privacy.announce.set', { tier: tierLabel(tier) }),
  )
}

/** Wipe the local visit log after a confirmation prompt, then refresh
 * the button and announce. The Continue-exploring row + Recently-viewed
 * chip update live via visitMemory's onVisitsChange listener. No
 * telemetry — visit memory is local-only, so clearing it isn't an
 * event we report. */
function handleClearVisits(): void {
  if (visitCount() === 0) return
  const confirmed =
    typeof window !== 'undefined' && typeof window.confirm === 'function'
      ? window.confirm(t('privacy.clearVisits.confirm'))
      : true
  if (!confirmed) return
  clearVisits()
  syncClearVisitsButton()
  announce(t('privacy.clearVisits.announce'))
}

function announce(message: string): void {
  const status = document.getElementById('privacy-ui-status')
  if (!status) return
  status.textContent = message
}

/** Ensure the panel exists in the DOM and its listeners are wired. */
function ensureMounted(): void {
  if (mounted) return
  buildPanel()
  wireEvents()
  mounted = true
}

/** Open the privacy settings panel. Opens lazily on first call. */
export function openPrivacyUI(triggeredBy?: HTMLElement | null): void {
  ensureMounted()
  syncRadios()
  syncSessionId()
  syncClearVisitsButton()
  syncVisitsSwitch()
  const panel = document.getElementById('privacy-ui-panel')
  const backdrop = document.getElementById('privacy-ui-backdrop')
  panel?.classList.remove('hidden')
  backdrop?.classList.remove('hidden')
  panel?.setAttribute('aria-hidden', 'false')
  backdrop?.setAttribute('aria-hidden', 'false')
  isOpen = true
  lastTrigger = triggeredBy ?? null
  // Focus the currently-selected radio so keyboard users land inside
  // the dialog on the right option.
  const selected = document.querySelector<HTMLInputElement>(
    'input[name="privacy-ui-tier"]:checked',
  )
  selected?.focus()
}

/** Close the panel and return focus to whatever opened it. */
export function closePrivacyUI(): void {
  const panel = document.getElementById('privacy-ui-panel')
  const backdrop = document.getElementById('privacy-ui-backdrop')
  panel?.classList.add('hidden')
  backdrop?.classList.add('hidden')
  panel?.setAttribute('aria-hidden', 'true')
  backdrop?.setAttribute('aria-hidden', 'true')
  isOpen = false
  lastTrigger?.focus()
  lastTrigger = null
}

/** Whether the panel is currently open. Used by tests. */
export function isPrivacyUIOpen(): boolean {
  return isOpen
}

/** Tear down listeners and DOM. Idempotent. Used by tests. */
export function disposePrivacyUI(): void {
  listenerController?.abort()
  listenerController = null
  const host = document.getElementById('privacy-ui-host')
  host?.remove()
  mounted = false
  isOpen = false
  lastTrigger = null
}
